import {useInjection} from "inversify-react";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import DetailSubscription from "@dropDesk/presentation/components/detail_subscription";
import {PaymentEntity} from "@dropDesk/domain/entities/payment/payment.entity";
import {observer} from "mobx-react";
import {
    AlertBanner,
    Button,
    Card,
    CardHeader,
    CardHeaderLeft,
    ContainerButtons,
    ContentStatus,
    ContentValue,
    Divider,
    ExpireDateText,
    Grid,
    IconWrapper,
    MessagePanel,
    PeriodText,
    ProductName,
    ProductSubtitle,
    ProductTextBlock,
} from "@dropDesk/presentation/pages/subscription/ui/detail_subscription/pending_payments/styled";
import {copyToClipboard, formatCurrency} from "@dropDesk/utils/helpers/common";
import {SharedColors} from "@dropDesk/domain/entities/theme/app_colors";
import toastMessage from "@dropDesk/utils/toast_message/toast_message";
import {ICONS_DROPDESK} from "@dropDesk/storage/icons/icons.d";
import DropDeskIcon from "@dropDesk/storage/icons";
import React from "react";
import {PaymentSubscriptionEntity} from "@dropDesk/domain/entities/payment_subscriptions/payment_subscription_entity";
import {formatDateToDDMMYYYY} from "@dropDesk/utils/helpers/date_helper";
import {PaymentItemType} from "@dropDesk/domain/entities/payment/payment.enum";


const PendingPayments = observer((
    {
        payments,
        handleClickPayWithPix,
        onClickDownloadInvoice,
        currentSubscription
    }: {
        payments: PaymentEntity[]
        handleClickPayWithPix: (payment: PaymentEntity) => void
        onClickDownloadInvoice: (payment: PaymentEntity) => void
        currentSubscription: PaymentSubscriptionEntity
    }
) => {

    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;

    return (
        <DetailSubscription
            title={'Cobranças Pendentes'}
            description={'Pagamentos pendentes da sua conta.'}
            style={{marginTop: 26}}
            content={
                <Grid>
                    {payments.map(payment => {
                        const hasPlan = payment.items?.some(item => item.type === PaymentItemType.plan) ?? false;
                        const hasExtension = payment.items?.some(item => item.type === PaymentItemType.extension) ?? false;
                        const isRejected = payment.rejected;
                        const isMixed = hasPlan && hasExtension;
                        const isPlanUpgrade = (payment.items?.length ?? 0) > 1 && !hasExtension;
                        const icon = hasExtension && !hasPlan ? ICONS_DROPDESK.extension : ICONS_DROPDESK.subscription;
                        const typeLabel = hasExtension && !hasPlan ? 'Extensão' : 'Plano de assinatura';
                        const productName = isMixed
                            ? 'Assinatura + Extensões'
                            : isPlanUpgrade
                                ? 'Upgrade de plano'
                                : payment.planDescription;

                        return (
                        <Card
                            key={payment.id}
                            borderColor={isRejected ? colors.textError : colors.border}
                            background={isRejected ? `${colors.textError}0D` : colors.onBackground}
                            highlight={isRejected}
                        >
                            {isRejected && (
                                <AlertBanner background={`${colors.textError}1A`} color={colors.textError}>
                                    <DropDeskIcon size={16} icon={ICONS_DROPDESK.warning} color={colors.textError}/>
                                    Pagamento recusado. Tente novamente para evitar a suspensão dos serviços.
                                </AlertBanner>
                            )}
                            <CardHeader>
                                <CardHeaderLeft>
                                    <IconWrapper background={`${colors.accent}12`} color={colors.accent}>
                                        <DropDeskIcon icon={icon} size={15} color={colors.accent}/>
                                    </IconWrapper>
                                    <ProductTextBlock>
                                        <ProductName color={colors.text}>{productName}</ProductName>
                                        <ProductSubtitle color={colors.hint}>{typeLabel}</ProductSubtitle>
                                    </ProductTextBlock>
                                </CardHeaderLeft>
                                <ContentStatus
                                    background={payment.paymentExpirationImminent || payment.rejected ? SharedColors.onCancelStatus : SharedColors.onAttendingStatus}
                                    color={payment.paymentExpirationImminent || payment.rejected ? SharedColors.textCancelStatus : SharedColors.textAttendingStatus}
                                >
                                    {payment.titlePanel(currentSubscription)}
                                </ContentStatus>
                            </CardHeader>

                            <Divider color={colors.border}/>

                            <ContentValue color={colors.text}>
                                {formatCurrency(payment.value)}
                            </ContentValue>

                            {!!payment.periodFormatted && (
                                <PeriodText color={colors.hint}>
                                    <DropDeskIcon size={14} icon={ICONS_DROPDESK.calendar} color={colors.hint}/>
                                    {payment.periodFormatted}
                                </PeriodText>
                            )}

                            {!!payment.expireDate && (
                                <ExpireDateText color={colors.hint}>
                                    <DropDeskIcon size={14} icon={ICONS_DROPDESK.clock} color={colors.hint}/>
                                    Expira em {formatDateToDDMMYYYY(new Date(payment.expireDate))}
                                </ExpireDateText>
                            )}

                            <MessagePanel color={colors.text}>
                                {payment.messagePanel(currentSubscription)}
                            </MessagePanel>

                            <ContainerButtons>
                                <Button
                                    onClick={() => handleClickPayWithPix(payment)}
                                    size={12}
                                    color={SharedColors.white}
                                    background={colors.accent}
                                    accent={colors.accent}
                                >
                                    <DropDeskIcon size={18} icon={ICONS_DROPDESK.pix} color={SharedColors.white}/>
                                    Pagar com pix
                                </Button>

                                {payment.isBoleto &&
                                    payment.boleto?.digitableLine &&
                                    payment.pix?.qrcodeText &&
                                    <Button
                                        onClick={() => {
                                            copyToClipboard(payment.boleto!.digitableLine);
                                            toastMessage.success('Código de barras copiado para área de transferência.');
                                        }}
                                        size={12}
                                        color={colors.accent}
                                        background={'transparent'}
                                        accent={colors.accent}
                                    >
                                        <DropDeskIcon size={18} icon={ICONS_DROPDESK.billet} color={colors.accent}/>
                                        Copiar cód. de barras
                                    </Button>
                                }

                                {!!payment.urlDocument &&
                                    <Button
                                        onClick={() => onClickDownloadInvoice(payment)}
                                        size={12}
                                        color={colors.accent}
                                        background={'transparent'}
                                        accent={colors.accent}
                                    >
                                        <DropDeskIcon size={18} icon={ICONS_DROPDESK.download} color={colors.accent}/>
                                        Baixar fatura
                                    </Button>
                                }
                            </ContainerButtons>
                        </Card>
                        );
                    })}
                </Grid>
            }
        />
    );
});

export default PendingPayments;